const get = require('lodash/get');
const clone = require('lodash/clone');
const { config } = require('@sstdev/lib_metadata-config');
const { dataTypeMapping } = config;

// private members:
const dataDictionary = Symbol('dataDictionary');

// constant values
const consumes = ['application/json'];
const produces = ['application/json'];
const securityResponses = {
    403: {
        description: 'Log in before attempting this operation.'
    }
};
const postResponses = Object.assign(
    {
        204: {
            description: 'Success. No content to return.'
        }
    },
    securityResponses
);
const getResponses = Object.assign(
    {
        200: {
            description: 'Success.'
        },
        204: {
            description: 'Success. No content to return.'
        },
        304: {
            description: 'Success. No content to return, when passing in `if-modified-since` header'
        }
    },
    securityResponses
);
const patchResponses = Object.assign(
    {
        204: {
            description: 'Success. No content to return.'
        }
    },
    {
        409: {
            description: 'Conflict.  It is likely another user updated this data after you received it.'
        }
    },
    securityResponses
);
const deleteResponses = Object.assign(
    {
        204: {
            description: 'Success. No content to return.'
        }
    },
    {
        400: {
            description:
                'Failure - usually because the resource being requested for deletion is in use by another resource in the application.'
        }
    },
    securityResponses
);

const EXCLUDED_PATHS = ['/item/shiftSummary/aggregate'];

const prettyNamespaceRelation = function (namespace, relation, plural = false) {
    const pluralization = plural && relation._meta.prettyName[relation._meta.prettyName.length - 1] !== 's' ? 's' : '';
    return namespace._meta.prettyName === relation._meta.prettyName ||
        relation._meta.prettyName.startsWith(namespace._meta.prettyName)
        ? relation._meta.prettyName + pluralization
        : `${namespace._meta.prettyName} ${relation._meta.prettyName + pluralization}`;
};

/**
 * Generate a swagger document
 * see https://swagger.io/docs/specification/2-0/basic-structure/ and https://swagger.io/specification/v2/ for more information
 */
module.exports = class SwaggerService {
    constructor(role, dictionary) {
        if (!dictionary) {
            throw new Error('A Data Dictionary must be specified when constructing the Swagger service.');
        }
        this[dataDictionary] = clone(dictionary);
    }

    getSwaggerDoc(forDocs = false) {
        const description =
            get(this[dataDictionary], '_meta.description', '') +
            '\n\nWhen invoked through our in-app API page, calls will be automatically authenticated using the logged-in user.\n\n When writing your own code, a JWT (JavaScript web token) must be provided as an `Authorization` header to authenticate with the application.' +
            ' It is recommended to use a personal access token. For detailed instructions on how to use the client id and secret obtained through that, see E.g. https://auth0.com/docs/authorization/flows/call-your-api-using-the-client-credentials-flow#steps.' +
            ' For using your own username and password, see the Security\'s `POST /security/auth` "tokenKey" value in the documentation below or look at an XHR header in the application to obtain your value.  Unless otherwise defined, tokens expire in 2 days.' +
            '\n\nTo increase the probability of conflict free simultaneous editing, this API supports the JSON patch specification (https://tools.ietf.org/html/rfc6902) rather than PUT operations.' +
            '\n\n`_id` values in the application are serialized BSON ObjectIDs (see examples below) (https://docs.mongodb.com/manual/reference/bson-types/#objectid)';

        let doc = {
            swagger: '2.0',
            info: {
                description,
                version: get(this[dataDictionary], '_meta.release', '1.0.0'),
                title: this[dataDictionary]._meta.title
            },
            basePath: '/api',
            schemes: ['https', 'http'],
            // add the patch and aggregate definition(schema):
            definitions: this.getDefaultDefinitions()
        };
        //create the "chapters" (namespaces) for the swagger doc
        doc.tags = this.createTags(forDocs);
        doc.paths = {};
        //now build the actual swagger content, for each ns/rel/operation, get the schema
        //note actual extraction of the schema happens in `this.getProperties()`
        this.createPaths(doc, forDocs);

        //manually filter out the few special paths we know we don't want to expose
        doc.paths = Object.entries(doc.paths).reduce((acc, [key, value]) => {
            if (EXCLUDED_PATHS.includes(key)) return acc;
            return { ...acc, [key]: value };
        }, {});
        return doc;
    }

    createTags(forDocs = false) {
        let tags = [];
        Object.keys(this[dataDictionary])
            .filter(ns => ns !== '_meta')
            .sort()
            .forEach(namespaceTitle => {
                // Create tag only if the namespace contains relations that are not exceptions.
                if (!doesNamespaceContainRelationsThatAreNotExceptions(this[dataDictionary], namespaceTitle)) return;
                let namespace = this[dataDictionary][namespaceTitle];
                if (forDocs && (!namespace._meta || namespace._meta.hideApiDocs)) return;
                tags.push({
                    name: namespace._meta.prettyName,
                    description: namespace._meta.description || ''
                });
            });
        return tags;

        function doesNamespaceContainRelationsThatAreNotExceptions(dictionary, namespaceTitle) {
            // If it isn't in the exceptions at all, then true.
            if (!config.allowedNamespaceRelationExceptions[namespaceTitle]) return true;
            // If the namespace is an exception, but it still contains a relation that IS NOT
            // an exception, then return true.
            return Object.keys(dictionary[namespaceTitle])
                .filter(ns => ns !== '_meta')
                .some(
                    relationTitle =>
                        config.allowedNamespaceRelationExceptions[namespaceTitle][relationTitle] === undefined
                );
        }
    }

    createPaths(document, forDocs = false) {
        Object.keys(this[dataDictionary]).forEach(namespaceTitle => {
            if (namespaceTitle === '_meta') return;
            let dictionaryNamespace = this[dataDictionary][namespaceTitle];
            // Do not create paths for hidden namespaces, or when we'd be using a custom API.
            if (
                forDocs &&
                (!dictionaryNamespace._meta ||
                    dictionaryNamespace._meta.hideApiDocs ||
                    dictionaryNamespace._meta.withCustomApi)
            )
                return;
            Object.keys(dictionaryNamespace).forEach(relationTitle => {
                if (relationTitle === '_meta') return;
                // Do not create paths for exception namespace/relations
                if (
                    config.allowedNamespaceRelationExceptions[namespaceTitle] &&
                    config.allowedNamespaceRelationExceptions[namespaceTitle][relationTitle]
                )
                    return;
                let dictionaryRelation = dictionaryNamespace[relationTitle];
                let relationBasePath = `/${namespaceTitle}/${relationTitle}`;

                let pathContext = {
                    dictionaryNamespace,
                    dictionaryRelation,
                    relationBasePath,
                    namespaceTitle,
                    relationTitle,
                    paths: document.paths,
                    definitions: document.definitions
                };

                this.createPostPath(pathContext);
                this.createGetPath(pathContext);
                this.createPatchPath(pathContext);
                this.createRemovePath(pathContext);
            });
        });
    }

    createRemovePath(pathContext) {
        let { dictionaryNamespace, dictionaryRelation, relationBasePath, namespaceTitle, relationTitle } = pathContext;
        if (dictionaryRelation._meta.accessContext.remove.value) {
            let idPath = (pathContext.paths[relationBasePath + '/{id}'] =
                pathContext.paths[relationBasePath + '/{id}'] || {});
            idPath.delete = {
                tags: [dictionaryNamespace._meta.prettyName],
                summary: `Remove a single ${prettyNamespaceRelation(
                    dictionaryNamespace,
                    dictionaryRelation
                )} using its Id.`,
                operationId: `delete_${namespaceTitle}_${relationTitle}_byId`,
                consumes,
                produces,
                responses: deleteResponses,
                parameters: [
                    {
                        in: 'path',
                        name: 'id',
                        description: `The id of the ${prettyNamespaceRelation(
                            dictionaryNamespace,
                            dictionaryRelation
                        )}`,
                        required: true,
                        type: 'string'
                    }
                ]
            };
        }
    }

    createGetPath(pathContext) {
        let { dictionaryNamespace, dictionaryRelation, relationBasePath, namespaceTitle, relationTitle } = pathContext;
        if (dictionaryRelation._meta.accessContext.get.value) {
            pathContext.operation = 'get';
            let basePath = (pathContext.paths[relationBasePath] = pathContext.paths[relationBasePath] || {});
            basePath.get = {
                tags: [dictionaryNamespace._meta.prettyName],
                summary: `Get a page of ${prettyNamespaceRelation(dictionaryNamespace, dictionaryRelation, true)}.`,
                description:
                    'Use either `page` + `pageSize`, or `offset` + `limit`. If both are provided, offset/limit will take precedence.<br/>' +
                    'Similarly, use either `searchTerm` + `exact` or `searchTermLowerBound` + `searchTermUpperBound`.<br/>When both are provided, the Lower and upperBound are ignored.' +
                    'Examples:<br/>' +
                    `- Find ${dictionaryRelation._meta.prettyName}s 20 through 29 with a title or description equal to "1001":<br/>` +
                    `&nbsp;&nbsp;\`/api${relationBasePath}?offset=20&limit=10&searchTerm=1001&searchProperties=%5B%22title%22,%22description%22%5D&exact=true\`<br/>` +
                    `- Find ${dictionaryRelation._meta.prettyName}s 10 through 20 that were last modified between Sep 15, 2018 00:00:00 and Oct 14, 2018 23:59:59:<br/>` +
                    `&nbsp;&nbsp;\`/api${relationBasePath}?page=2&pageSize=10&searchTermLowerBound=2018-09-15T00%3A00%3A00&searchTermUpperBound=2018-10-14T23%3A59%3A59&searchProperties=%5B%22meta.lastModified%22%5D\`<br/>` +
                    'Note: Requesting a page beyond 20,000 records will result in an error.</br>',
                operationId: `get_${namespaceTitle}_${relationTitle}`,
                consumes,
                produces,
                responses: Object.assign({}, getResponses, {
                    200: {
                        description: 'Success.',
                        schema: inItemsArrayObject(this.getGetSuccessDefinition(pathContext))
                    },
                    500: {
                        description:
                            'An Error occured retrieving the data. Will occur when requesting beyond 20,000 records.'
                    }
                }),
                parameters: [
                    {
                        in: 'query',
                        name: 'correlationId',
                        description: 'Generated by SST clients to allow additional analysis of requests.',
                        required: false,
                        type: 'string'
                    },
                    {
                        in: 'query',
                        name: 'offset',
                        description: 'The number of items to skip before returning remaining results (0-based)',
                        required: false,
                        type: 'integer',
                        default: 0
                    },
                    {
                        in: 'query',
                        name: 'limit',
                        description:
                            'Similar to pageSize (number of items to return per request), but only used in conjunction with offset',
                        required: false,
                        type: 'integer',
                        default: 100
                    },
                    {
                        in: 'query',
                        name: 'page',
                        description: 'The page of results to get (1-based)',
                        required: false,
                        type: 'integer',
                        default: 1
                    },
                    {
                        in: 'query',
                        name: 'pageSize',
                        description: 'The number of items per page of results',
                        required: false,
                        type: 'integer',
                        default: 25
                    },
                    {
                        in: 'query',
                        name: 'orderBy',
                        description:
                            'The property in the results by which the results will be sorted.  Add suffix of ":a" for ascending sort or suffix of ":d" for descending.',
                        required: false,
                        type: 'string',
                        default: 'title:a'
                    },
                    {
                        in: 'query',
                        name: 'searchTerm',
                        description:
                            'A value used to filter the results.  All `searchProperties` fields will be searched for the value.',
                        required: false
                    },
                    {
                        in: 'query',
                        name: 'searchTermLowerBound',
                        description:
                            'A date used to filter the results from.  All `searchProperties` fields will be searched for a date larger than this.',
                        required: false
                    },
                    {
                        in: 'query',
                        name: 'searchTermUpperBound',
                        description:
                            'A date used to filter the results to.  All `searchProperties` fields will be searched for a date smaller than this.',
                        required: false
                    },
                    {
                        in: 'query',
                        name: 'exact',
                        description: 'Specify if searchTerm is an exact match',
                        required: false,
                        type: 'boolean'
                    },
                    {
                        in: 'query',
                        name: 'excludeProperty',
                        description: 'Property to filter out on',
                        required: false,
                        type: 'string'
                    },
                    {
                        in: 'query',
                        name: 'excludePropertyValues',
                        description: 'An array of excluded values',
                        required: false,
                        type: 'array',
                        items: {
                            type: 'string'
                        },
                        example: '%5B%22deleted%22,%22closed%22%5D'
                    },
                    {
                        in: 'query',
                        name: 'includeDeleted',
                        description: 'Specify if deleted records should be included in the result',
                        required: false,
                        type: 'boolean'
                    },
                    {
                        in: 'query',
                        name: 'searchProperties',
                        description:
                            'an array of field paths to search in.<br/> E.g. `assetNo,description,location:location.title`',
                        required: false,
                        type: 'array',
                        items: {
                            type: 'string'
                        },
                        example: '%5B%22item:item.title%22,%22tem:item.description%22%5D'
                    },
                    {
                        in: 'query',
                        name: 'criteria',
                        description: 'Specify search criteria as json object',
                        required: false,
                        type: 'string'
                    }
                ]
            };
            let aggregatePath = (pathContext.paths[relationBasePath + '/aggregate'] = {});
            aggregatePath.get = {
                tags: [dictionaryNamespace._meta.prettyName],
                summary: `Get counts over ${prettyNamespaceRelation(dictionaryNamespace, dictionaryRelation, true)}.`,
                description:
                    'Use either `searchTerm` + `exact` or `searchTermLowerBound` + `searchTermUpperBound`.<br/>When both are provided, the Lower and upperBound are ignored.',
                operationId: `get_${namespaceTitle}_${relationTitle}_aggregate`,
                consumes,
                produces,
                responses: Object.assign(
                    {},
                    {
                        200: {
                            description: 'Success.',
                            schema: inItemsArrayObject('#/definitions/aggregate')
                        }
                    }
                ),
                parameters: [
                    {
                        in: 'query',
                        name: 'groupBy',
                        description: `The property of the ${dictionaryRelation._meta.prettyName} for which the results will be aggregated, e.g. \`title\`. Leave blank for a total count.`,
                        required: false,
                        type: 'string'
                    },
                    {
                        in: 'query',
                        name: 'searchTerm',
                        description:
                            'A value used to filter the results.  All `searchProperties` fields will be searched for the value.',
                        required: false
                    },
                    {
                        in: 'query',
                        name: 'exact',
                        description: 'Specify if searchTerm is an exact match',
                        required: false,
                        type: 'boolean'
                    },
                    {
                        in: 'query',
                        name: 'searchTermLowerBound',
                        description:
                            'A date used to filter the results from.  All `searchProperties` fields will be searched for a date larger than this.',
                        required: false
                    },
                    {
                        in: 'query',
                        name: 'searchTermUpperBound',
                        description:
                            'A date used to filter the results to.  All `searchProperties` fields will be searched for a date smaller than this.',
                        required: false
                    },
                    {
                        in: 'query',
                        name: 'searchProperties',
                        description: 'an array of (string) field paths to search on.',
                        required: false,
                        type: 'array',
                        items: {
                            type: 'string'
                        },
                        example: '["item:item.title","item:item.description"]'
                    },
                    {
                        in: 'query',
                        name: 'includeDeleted',
                        description: 'Specify if deleted records should be included in the result',
                        required: false,
                        type: 'boolean'
                    }
                ]
            };
            let idPath = (pathContext.paths[relationBasePath + '/{id}'] = {});
            idPath.get = {
                tags: [dictionaryNamespace._meta.prettyName],
                summary: `Get a single ${prettyNamespaceRelation(
                    dictionaryNamespace,
                    dictionaryRelation
                )} using its Id.`,
                operationId: `get_${namespaceTitle}_${relationTitle}_byId`,
                consumes,
                produces,
                responses: Object.assign({}, getResponses, {
                    200: {
                        description: 'Success.',
                        schema: {
                            $ref: this.getGetSuccessDefinition(pathContext)
                        }
                    },
                    204: {
                        description: 'Success, but no results match the requested query or page.'
                    }
                }),
                parameters: [
                    {
                        in: 'path',
                        name: 'id',
                        description: `The id of the ${prettyNamespaceRelation(
                            dictionaryNamespace,
                            dictionaryRelation
                        )}`,
                        required: true,
                        type: 'string'
                    }
                ]
            };
        }
    }

    createPostPath(pathContext) {
        let { dictionaryNamespace, dictionaryRelation, relationBasePath, namespaceTitle, relationTitle } = pathContext;
        if (dictionaryRelation._meta.accessContext.post.value) {
            pathContext.operation = 'post';
            let basePath = (pathContext.paths[relationBasePath] = pathContext.paths[relationBasePath] || {});
            basePath.post = {
                tags: [dictionaryNamespace._meta.prettyName],
                summary: `Insert your new ${prettyNamespaceRelation(dictionaryNamespace, dictionaryRelation)}.`,
                operationId: `post_${namespaceTitle}_${relationTitle}`,
                consumes,
                produces,
                responses: postResponses,
                parameters: [
                    {
                        in: 'body',
                        name: 'body',
                        description: `The ${prettyNamespaceRelation(
                            dictionaryNamespace,
                            dictionaryRelation
                        )} that will be sent for insertion`,
                        required: true,
                        schema: { $ref: this.getPostDefinition(pathContext) }
                    }
                ]
            };
        }
    }

    createPatchPath(pathContext) {
        let { dictionaryNamespace, dictionaryRelation, relationBasePath, namespaceTitle, relationTitle } = pathContext;
        if (dictionaryRelation._meta.accessContext.patch.value) {
            let idPath = (pathContext.paths[relationBasePath + '/{id}'] =
                pathContext.paths[relationBasePath + '/{id}'] || {});
            idPath.patch = {
                tags: [dictionaryNamespace._meta.prettyName],
                summary: `Patch this ${
                    dictionaryNamespace._meta.prettyName === dictionaryRelation._meta.prettyName
                        ? ''
                        : dictionaryNamespace._meta.prettyName
                } ${dictionaryRelation._meta.prettyName}.`,
                operationId: `patch_${namespaceTitle}_${relationTitle}`,
                consumes,
                produces,
                responses: patchResponses,
                parameters: [
                    {
                        in: 'path',
                        name: 'id',
                        description: `The id of the ${prettyNamespaceRelation(
                            dictionaryNamespace,
                            dictionaryRelation
                        )}`,
                        required: true,
                        type: 'string'
                    },
                    {
                        in: 'body',
                        name: 'body',
                        description: `An array of JSON patches for the ${prettyNamespaceRelation(
                            dictionaryNamespace,
                            dictionaryRelation
                        )} as defined in https://tools.ietf.org/html/rfc6902.  Move and Copy operations are not supported.  Instead use replace. Use test operations to ensure conflicting changes will cause the request to fail.`,
                        required: true,
                        schema: { $ref: this.getPatchDefinition(pathContext) }
                    }
                ]
            };
        }
    }

    getRequired(definitionContext) {
        let { dictionaryRelation, dictionaryProperty } = definitionContext;
        let propertyContainer = dictionaryProperty || dictionaryRelation; // prefer property if given
        let required = Object.keys(propertyContainer).filter(key => {
            return key !== '_meta' && propertyContainer[key]._meta.required;
        });
        return required.length > 0 ? required : undefined; // cause property not to appear if none are required.
    }

    //this is where the real heart of it is.
    //Extract all the properties of the ns/rel, based on access, and operation
    getProperties(definitionContext) {
        let { dictionaryRelation, dictionaryProperty } = definitionContext;
        let propertyContainer = dictionaryProperty || dictionaryRelation; // prefer property if given
        let properties = {};
        Object.keys(propertyContainer)
            .filter(p => p !== '_meta')
            .forEach(propertyName => {
                let dictionaryProperty = propertyContainer[propertyName];
                // add property if we have access to it.
                if (dictionaryProperty._meta.accessContext[definitionContext.operation].value) {
                    let newDefinitionContext = Object.assign({}, definitionContext, { dictionaryProperty });

                    let swaggerProperty = {};
                    swaggerProperty = this.withJsonSchemaDataType(swaggerProperty, newDefinitionContext);
                    swaggerProperty = withValidateInfo(swaggerProperty, dictionaryProperty._meta);
                    swaggerProperty.description = dictionaryProperty._meta.title;
                    let required = this.getRequired(newDefinitionContext) || [];
                    if (swaggerProperty?.properties?._id) {
                        required.push('_id');
                    }
                    if (required.length > 0) {
                        swaggerProperty.required = required;
                    }
                    properties[propertyName] = swaggerProperty;
                }
            });

        //some "child objects" don't have id's, for everything else, add an _id
        if (
            propertyContainer._meta &&
            !['workflowCriteriaElement', 'workflowPane'].includes(propertyContainer._meta.hNodeTypeGroup) &&
            !['EditableList'].includes(propertyContainer._meta.hNodeType)
        ) {
            properties._id = {
                type: 'string',
                format: 'BSON ObjectID',
                maxLength: 24,
                minLength: 24,
                pattern: '^[0-9a-f]*$',
                example: '12344ac4ac34d504212c0db6'
            };
        }

        return properties;
    }

    getDefaultDefinitions() {
        return {
            aggregate: {
                type: 'object',
                description: `The result of an aggregate operation:<br/>
                Example:
                    <pre>[
                        {
                            "_id": {
                                "title": "Total"
                            },
                            "count": 16
                        }
                    ]</pre>`,
                required: ['_id', 'count'],
                properties: {
                    _id: {
                        type: 'object',
                        properties: {
                            title: {
                                type: 'string',
                                description: 'The value of the groupBy property, or "Total" if none specified'
                            }
                        }
                    },
                    count: { type: 'integer' }
                }
            },
            patch: {
                type: 'object',
                description:
                    "Examples:<br/>{ 'op': 'test', 'path': '/title', 'value': 'foo' }<br/>{ 'op': 'remove', 'path': 'item:status' }<br/>{ 'op': 'add', 'path': 'location:location', 'value': {'title':'here'} }<br/>{ 'op': 'replace', 'path': 'antennaConfiguration/0/cooldown', 'value': 42 }",
                required: ['op', 'path', 'value'],
                properties: {
                    op: {
                        type: 'string',
                        enum: ['test', 'add', 'remove', 'replace'],
                        description: 'One of `test`, `add`, `remove` ,`replace`'
                    },
                    path: {
                        type: 'string',
                        description:
                            "The document path pointer where this operation should be applied as described in https://tools.ietf.org/html/rfc6901.  See this relation's POST model for the document structure.",
                        example:
                            '/title - points to the title property at the root of a document. | /someArray/0/title - points to the title property of the first object in the `someArray` array property of the document.'
                    },
                    value: {
                        example: 'Location 1',
                        description:
                            'A value (type as applicable for the given path) for testing or storing at the given path.'
                    }
                }
            }
        };
    }

    getPatchDefinition(definitionContext) {
        definitionContext.definitions = definitionContext.definitions || {};
        let { namespaceTitle, relationTitle } = definitionContext;
        let definitionName = `PATCH_${namespaceTitle}_${relationTitle}`;

        if (!definitionContext.definitions[definitionName]) {
            definitionContext.definitions[definitionName] = {
                type: 'array',
                items: {
                    $ref: '#/definitions/patch'
                }
            };
        }

        return '#/definitions/' + definitionName;
    }

    getCoordinates(definitionContext) {
        definitionContext.definitions = definitionContext.definitions || {};
        const definitionName = 'COORDINATES';
        if (!definitionContext.definitions[definitionName]) {
            definitionContext.definitions[definitionName] = {
                type: 'object',
                description: `A pair of coordinates, such as GPS latitude and longitude or x/y Euclidian coordinates. <br/>
                Example: { firstCoordinate: 34.081231, secondCoordinate: -84.162460 }`,
                properties: {
                    firstCoordinate: {
                        type: 'number'
                    },
                    secondCoordinate: {
                        type: 'number'
                    }
                },
                required: ['firstCoordinate', 'secondCoordinate']
            };
        }
        return '#/definitions/' + definitionName;
    }

    getHierarchyParent(definitionContext) {
        definitionContext.definitions = definitionContext.definitions || {};
        let { namespaceTitle, relationTitle } = definitionContext;
        let definitionName = `HIERARCHY_PARENT_${namespaceTitle}_${relationTitle}`;

        if (!definitionContext.definitions[definitionName]) {
            definitionContext.definitions[definitionName] = {
                type: 'object',
                properties: {
                    [`${namespaceTitle}:${relationTitle}`]: {
                        type: 'object',
                        properties: {
                            _id: {
                                type: 'object',
                                properties: {
                                    $oid: { type: 'string' },
                                    format: 'BSON ObjectID',
                                    maxLength: 24,
                                    minLength: 24,
                                    pattern: '^[0-9a-f]*$',
                                    example: '12344ac4ac34d504212c0db6'
                                },
                                required: ['$oid']
                            }
                        },
                        required: ['_id']
                    }
                }
            };
        }

        return '#/definitions/' + definitionName;
    }

    getGetSuccessDefinition(definitionContext) {
        let { namespaceTitle, relationTitle, dictionaryProperty } = definitionContext;
        let definitionName = `GET_${namespaceTitle}_${relationTitle}_SUCCESS`;
        if (dictionaryProperty) {
            definitionName = `${definitionName}_${dictionaryProperty._meta.title}`;
        }

        // Lazy build definitions.
        if (!definitionContext.definitions[definitionName]) {
            let required = this.getRequired(definitionContext);
            definitionContext.definitions[definitionName] = {
                type: 'object',
                properties: this.getProperties(definitionContext),
                required
            };
        }

        return '#/definitions/' + definitionName;
    }

    getPostDefinition(definitionContext) {
        let { namespaceTitle, relationTitle, dictionaryProperty } = definitionContext;
        let definitionName = `POST_${namespaceTitle}_${relationTitle}`;
        if (dictionaryProperty) {
            definitionName = `${definitionName}_${dictionaryProperty._meta.title}`;
        }

        // Lazy build definitions.
        if (!definitionContext.definitions[definitionName]) {
            let required = this.getRequired(definitionContext);
            definitionContext.definitions[definitionName] = {
                type: 'object',
                properties: this.getProperties(definitionContext),
                required
            };
        }

        return '#/definitions/' + definitionName;
    }

    withJsonSchemaDataType(swaggerProperty, definitionContext) {
        let hNodeType = definitionContext.dictionaryProperty._meta.hNodeType;
        let type, format;
        if (hNodeType === 'InvisibleElement') {
            type = definitionContext.dictionaryProperty._meta.overrideDataType;
            format = definitionContext.dictionaryProperty._meta.overrideDataFormat;
        } else {
            // Find a data type (and possibly a format) for this hNodeType in the config
            Object.keys(dataTypeMapping).forEach(dt => {
                if (dataTypeMapping[dt].formats) {
                    Object.keys(dataTypeMapping[dt].formats).forEach(f => {
                        if (dataTypeMapping[dt].formats[f].hNodeTypes.includes(hNodeType)) {
                            type = dt;
                            format = f;
                        }
                    });
                } else {
                    if (dataTypeMapping[dt].hNodeTypes.includes(hNodeType)) {
                        type = dt;
                    }
                }
            });
        }
        if (!type && hNodeType === 'object') {
            type = hNodeType;
            // No need to set format here, we will recurse through the object properties
        }
        if (!type) {
            throw new Error(`${hNodeType} is not in dataTypeMapping.  Better fix that.`);
        }
        if (type === 'object') {
            if (format === 'hierarchyParent') {
                return { $ref: this.getHierarchyParent(definitionContext) };
            }
            if (format === 'coordinates') {
                return { $ref: this.getCoordinates(definitionContext) };
            }
            if (hNodeType === 'WorkflowActionPane') {
                return Object.assign(
                    {},
                    swaggerProperty,
                    {
                        type: 'object'
                    },
                    this.getProperties(definitionContext) //recursive call
                );
            }
            return Object.assign({}, swaggerProperty, {
                type: 'object',
                properties: this.getProperties(definitionContext) //recursive call
            });
        }
        if (hNodeType === 'WorkflowActionList') {
            return this.getMultiItemArrayReference(swaggerProperty, definitionContext);
        }
        if (type === 'array') {
            switch (format) {
                case 'string':
                    return Object.assign({}, swaggerProperty, {
                        type: 'array',
                        items: {
                            type: 'string'
                        }
                    });
                case 'object':
                    return this.getArrayReference(swaggerProperty, definitionContext);
                default:
                    throw new Error(
                        `${hNodeType} with format ${format} is not in dataTypeMapping.  Not sure how you got here.  Better fix that.`
                    );
            }
        }

        return Object.assign({}, swaggerProperty, {
            type,
            format
        });
    }

    getMultiItemArrayReference(swaggerProperty, definitionContext) {
        let { dictionaryProperty } = definitionContext;
        let getDefinition =
            definitionContext.operation === 'get' ? this.getGetSuccessDefinition : this.getPostDefinition;
        getDefinition = getDefinition.bind(this);
        let refs = Object.keys(dictionaryProperty)
            .filter(p => p !== '_meta')
            .map(propertyName => {
                let childDictionaryProperty = dictionaryProperty[propertyName];
                // add property if we have access to it.
                if (childDictionaryProperty._meta.accessContext[definitionContext.operation].value) {
                    let newDefinitionContext = Object.assign({}, definitionContext, {
                        dictionaryProperty: childDictionaryProperty
                    });
                    return getDefinition(newDefinitionContext);
                }
            });

        return Object.assign({}, swaggerProperty, {
            type: 'array',
            items: {
                anyOf: refs.map(refString => {
                    return { $ref: refString };
                })
            }
        });
    }

    getArrayReference(swaggerProperty, definitionContext) {
        let ref = '';
        switch (definitionContext.operation) {
            case 'get':
                ref = this.getGetSuccessDefinition(definitionContext);
                break;
            default:
                ref = this.getPostDefinition(definitionContext);
                break;
        }

        return Object.assign({}, swaggerProperty, {
            type: 'array',
            items: {
                $ref: ref
            }
        });
    }
};

function withValidateInfo(property, hNodeType) {
    if (['workflowCriteriaElement', 'workflowPane'].includes(hNodeType.hNodeTypeGroup)) {
        //workflow controls have validation at a deeper level.
        return property;
    }
    let { minLength, maxLength, max, min } = hNodeType;
    let maximum = max;
    let minimum = min;
    return Object.assign({}, property, {
        minLength,
        maxLength,
        maximum,
        minimum
    });
}

function inItemsArrayObject(typeRef) {
    return {
        type: 'object',
        properties: {
            items: {
                type: 'array',
                items: {
                    $ref: typeRef
                }
            }
        }
    };
}
